import { useCallback, useContext, useRef, useState } from "react";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { Counterparty } from "@common/types/counterparty";
import {
    updateBillExtractionDetails,
    updateBillExtractions,
    updateInvoiceExtractionDetails,
    updateInvoiceExtractions,
    useFinancialDocumentFinalizeAccrualImportMutation,
} from "@api/financialDocument.api";
import { didRequestReachServer } from "@api/apiError";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { AccrualImporterContext } from "../accrualImporter.context";
import {
    getAllAccrualLineItemExtractions,
    mergeUpdatedAccrualExtraction,
} from "../accrualImporter.helpers";
import {
    AccrualDraftInvoiceFormSlice,
    accrualDraftMissingFields,
    blankInvoiceExtraction,
    invoiceExtractionFromFormState,
} from "./accrualDraftInvoiceMapping";

export function useConfirmAccrualDraft({
    financialDocument,
    extraction,
    state,
    totalAmount,
    taxAmount,
    discountAmount,
    depositAmount,
    entityId,
    onImported,
    onClose,
}: {
    financialDocument: FinancialDocumentExtended;
    extraction: InvoiceExtractionRecord | null;
    state: AccrualDraftInvoiceFormSlice;
    totalAmount: number;
    taxAmount: number;
    discountAmount: number;
    depositAmount: number;
    entityId: number | null;
    onImported: () => void;
    onClose: () => void;
}) {
    const { mode, revalidateDocument } = useContext(AccrualImporterContext);
    const finalizeImport =
        useFinancialDocumentFinalizeAccrualImportMutation(mode);
    const toaster = useToaster();
    const [isConfirming, setIsConfirming] = useState(false);
    const isConfirmingRef = useRef(false);

    const importDraft = useCallback(async () => {
        const nextExtraction = invoiceExtractionFromFormState({
            extraction: extraction ?? blankInvoiceExtraction(),
            state,
            totalAmount,
            taxAmount,
            discountAmount,
            depositAmount,
            entityId,
        });
        if (mode === "bills") {
            await updateBillExtractions({
                financialDocumentId: financialDocument.id,
                billExtractions: mergeUpdatedAccrualExtraction({
                    allExtractions: getAllAccrualLineItemExtractions(
                        financialDocument,
                        "bills",
                    ),
                    updatedExtraction: nextExtraction,
                }),
            });
        } else {
            await updateInvoiceExtractions({
                financialDocumentId: financialDocument.id,
                invoiceExtractions: mergeUpdatedAccrualExtraction({
                    allExtractions: getAllAccrualLineItemExtractions(
                        financialDocument,
                        "invoices",
                    ),
                    updatedExtraction: nextExtraction,
                }),
            });
        }
        await finalizeImport.mutateAsync([financialDocument.id]);
        revalidateDocument({ financialDocumentId: financialDocument.id });
    }, [
        depositAmount,
        discountAmount,
        entityId,
        extraction,
        finalizeImport,
        financialDocument,
        mode,
        revalidateDocument,
        state,
        taxAmount,
        totalAmount,
    ]);

    const runImport = useCallback(
        async (afterImport: () => void) => {
            if (isConfirmingRef.current) {
                return;
            }
            if (
                accrualDraftMissingFields({
                    entityId,
                    customerId: state.customer?.id ?? "",
                    invoiceNumber: state.invoiceNumber,
                    dueDate: state.dueDate,
                    lines: state.lines,
                }).length > 0
            ) {
                return;
            }
            isConfirmingRef.current = true;
            setIsConfirming(true);
            try {
                await importDraft();
                afterImport();
            } catch (error) {
                if (!didRequestReachServer(error)) {
                    toaster.error(
                        mode === "bills"
                            ? "Couldn't confirm this bill. Try again."
                            : "Couldn't confirm this invoice. Try again.",
                    );
                }
            } finally {
                isConfirmingRef.current = false;
                setIsConfirming(false);
            }
        },
        [
            entityId,
            importDraft,
            mode,
            state.customer?.id,
            state.dueDate,
            state.invoiceNumber,
            state.lines,
            toaster,
        ],
    );

    const confirm = useCallback(() => {
        void runImport(onImported);
    }, [onImported, runImport]);

    const confirmAndClose = useCallback(() => {
        void runImport(onClose);
    }, [onClose, runImport]);

    return { confirm, confirmAndClose, isConfirming };
}

interface SaveAccrualDraftDetailsParams {
    financialDocumentId: number;
    extractionId: string | null;
}

interface AccrualDraftDetailsPatch {
    counterpartyId?: string;
    counterpartyName?: string;
    documentNumber?: string | null;
    entityId?: number;
}

interface SaveAccrualDraftDetailsOptions {
    details: AccrualDraftDetailsPatch;
    errorMessage: string;
}

export type SaveAccrualDraftDetails = (
    options: SaveAccrualDraftDetailsOptions,
) => Promise<boolean>;

export function useSaveAccrualDraftDetails({
    financialDocumentId,
    extractionId,
}: SaveAccrualDraftDetailsParams): SaveAccrualDraftDetails {
    const { mode, revalidateDocument } = useContext(AccrualImporterContext);
    const toaster = useToaster();
    const queueRef = useRef<Promise<void>>(Promise.resolve());
    const pendingCountRef = useRef(0);

    return useCallback(
        async ({ details, errorMessage }: SaveAccrualDraftDetailsOptions) => {
            if (!extractionId) {
                return true;
            }
            const patchDetails = async () => {
                if (mode === "bills") {
                    await updateBillExtractionDetails({
                        ...details,
                        financialDocumentId,
                        billExtractionId: extractionId,
                    });
                } else {
                    await updateInvoiceExtractionDetails({
                        ...details,
                        financialDocumentId,
                        invoiceExtractionId: extractionId,
                    });
                }
            };
            pendingCountRef.current += 1;
            const request = queueRef.current.then(patchDetails);
            queueRef.current = request.catch(() => undefined);
            const awaitRequest = async () => {
                try {
                    await request;
                    return true;
                } catch {
                    toaster.error(errorMessage);
                    return false;
                }
            };
            return await awaitRequest().finally(() => {
                pendingCountRef.current -= 1;
                if (pendingCountRef.current === 0) {
                    revalidateDocument({ financialDocumentId });
                }
            });
        },
        [extractionId, financialDocumentId, mode, revalidateDocument, toaster],
    );
}

export function useSaveAccrualDraftCustomer(
    saveDetails: SaveAccrualDraftDetails,
) {
    const { mode } = useContext(AccrualImporterContext);

    return useCallback(
        async (counterparty: Counterparty) => {
            await saveDetails({
                details: {
                    counterpartyId: counterparty.id,
                    counterpartyName: counterparty.name,
                },
                errorMessage:
                    mode === "bills"
                        ? "Failed to update vendor"
                        : "Failed to update customer",
            });
        },
        [mode, saveDetails],
    );
}
